import {
  insertLanguage,
  removeLanguage,
  shouldSkipPreferredSelection,
  type LanguagePreferenceList,
} from '@core/selection.ts';
import type { TrackList } from '@core/model/track.ts';

/** Track row fields used by YouTube preferred-pick (extension `pickPreferredYoutubeTrackIndex`). */
export type PreferableSubtitle = TrackList['subtitles'][number] & {
  enhanced?: boolean;
  creatorEnhanced?: boolean;
  kind?: string;
  whisper?: boolean;
  restored?: boolean;
};

/**
 * Extension YouTube MRU pick:
 * - Type 1 ASR Enhanced: sticky when no plain creator matched yet
 * - Type 2 creator-enhanced: never sticky (prefer plain creator)
 * - Whisper / restored: skip
 */
export function pickPreferredTrackValue(
  subtitles: PreferableSubtitle[],
  languages: LanguagePreferenceList,
): number | undefined {
  if (shouldSkipPreferredSelection(languages) || languages.length === 0) {
    return undefined;
  }

  let track: number | undefined;
  let picked = false;

  for (let i = 0; i < languages.length; i += 1) {
    const languageCode = languages[i];
    if (picked || languageCode === undefined) break;

    for (let j = 0; j < subtitles.length; j += 1) {
      const entry = subtitles[j];
      if (!entry) continue;
      if (entry.whisper || entry.restored) continue;
      if (entry.creatorEnhanced === true) continue;
      const trackLanguageCode = entry.code;
      if (!trackLanguageCode) continue;

      if (languageCode === trackLanguageCode) {
        if (entry.enhanced === true) {
          const cur = track !== undefined
            ? subtitles.find((s) => s.value === track)
            : undefined;
          if (track === undefined || cur?.kind === 'asr') {
            track = entry.value;
            picked = true;
            break;
          }
          continue;
        }
        if (entry.kind !== 'asr') {
          track = entry.value;
        } else if (track === undefined) {
          track = entry.value;
        }
      }

      if (languageCode.includes(trackLanguageCode) || trackLanguageCode.includes(languageCode)) {
        if (entry.enhanced === true) {
          const cur = track !== undefined
            ? subtitles.find((s) => s.value === track)
            : undefined;
          if (track === undefined || cur?.kind === 'asr') {
            track = entry.value;
            picked = true;
            break;
          }
          continue;
        }
        if (entry.kind !== 'asr' && track === undefined) {
          track = entry.value;
        }
      }
    }

    if (track !== undefined) {
      picked = true;
    }
  }

  return track;
}

/**
 * Extension `saveSelected` language MRU:
 * - push code to front via insertLanguage
 * - when not off, drop the `'null'` sentinel
 * - off (`'null'`) keeps prior codes after the sentinel
 */
export function rememberLanguagePreference(
  languages: LanguagePreferenceList,
  languageCode: string,
): string[] {
  let next = insertLanguage(languages, languageCode);
  if (languageCode !== 'null') {
    next = removeLanguage(next, 'null');
  }
  return next;
}

/** Explicit “off” choice — persists across reload (extension parity). */
export const LANGUAGE_OFF_SENTINEL = 'null';

/**
 * Extension saveSelected when idx is undefined: insertLanguage(..., 'null').
 * Prefer {@link rememberLanguagePreference}(mru, 'null') so history is kept.
 */
export function languageOffPreference(
  languages: LanguagePreferenceList = [],
): string[] {
  return rememberLanguagePreference(languages, LANGUAGE_OFF_SENTINEL);
}

/**
 * Tracks that must not update subtitle language MRU (extension saveSelected):
 * uploaded `custom`, or whisper / missing language code.
 */
export function shouldSkipLanguageMru(languageCode: string | undefined): boolean {
  return !languageCode || languageCode === 'custom';
}

/** Extension popup `beautifySelections`: MRU exact-code matches first, then remainder. */
export function orderTranslationsByMru<T extends { code: string }>(
  translations: T[],
  mru: LanguagePreferenceList,
): T[] {
  if (!mru.length || translations.length <= 1) return translations;

  const front: T[] = [];
  for (let i = 0; i < mru.length; i += 1) {
    const code = mru[i];
    if (!code || code === LANGUAGE_OFF_SENTINEL) continue;
    const match = translations.find((tr) => tr.code === code);
    if (match && !front.includes(match)) {
      front.push(match);
    }
  }

  const rest = translations.filter((tr) => !front.includes(tr));
  return [...front, ...rest];
}

export function findTranslationByCode<T extends { code: string }>(
  translations: T[],
  code: string | undefined,
): T | undefined {
  if (!code || code === LANGUAGE_OFF_SENTINEL) return undefined;
  const lower = code.toLowerCase();
  return translations.find((tr) => tr.code.toLowerCase() === lower);
}

export { insertLanguage, removeLanguage, shouldSkipPreferredSelection };
